import { zodResolver } from "@hookform/resolvers/zod";
import {
  httpJoinWaitlist,
  httpWaitlistOptions,
  waitlistOptionsQueryKey,
} from "api/waitlist";
import Header from "components/layout/Header";
import Spinner from "components/ui/Spinner";
import WaitlistConfirmationModal from "components/ui/WaitlistConfirmationModal";
import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useForm } from "react-hook-form";
import { useQuery } from "react-query";
import { Link } from "react-router-dom";
import { publicRoutes } from "routes/routes";
import { z } from "zod";

const WHATSAPP_COMMUNITY_URL =
  "https://chat.whatsapp.com/FZCZhV4Hdap256dODgfT1N?mode=gi_t";
const TELEGRAM_COMMUNITY_URL = "https://t.me/+IpTx2GFZCOIxNjVk";
const BITOSHI_DOWNLOAD_URL = "https://bitoshi.onelink.me/nzWH/zanspi17";
const TWITTER_URL = "https://twitter.com/Bitoshiafrica";

export const waitlistSchema = z.object({
  whatsappNumber: z
    .string()
    .min(5, "WhatsApp number must be at least 5 characters")
    .regex(/^[0-9+()-\s]*$/, "Invalid phone number format"),
  fullName: z
    .string()
    .min(3, "Full name must be at least 3 characters")
    .max(200, "Full name is too long"),
  email: z.string().email("Please enter a valid email address"),
  referralTag: z.string().max(100, "Source of referral is too long"),
  tradeFrequency: z.string().min(1, "Please select your trading frequency"),
  primaryCryptoActivity: z
    .string()
    .min(1, "Please select your primary crypto activity"),
  monthlyVolumeRange: z
    .string()
    .min(1, "Please select your monthly trading volume"),
  frustrations: z
    .array(z.string())
    .min(1, "Please select at least one pain point"),
  removeForever: z
    .string()
    .min(5, "Please write at least 5 characters")
    .max(500, "Answer must be at most 500 characters"),
  agreeToUpdates: z.boolean().refine((val) => val === true, {
    message: "You must agree to updates to join the waitlist",
  }),
});

type WaitlistFormData = z.infer<typeof waitlistSchema>;

const V2Waitlist: React.FC = () => {
  const [isPending, setIsPending] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [waitlistPosition, setWaitlistPosition] = useState<number | null>(null);
  const [alreadyOnWaitlist, setAlreadyOnWaitlist] = useState(false);

  const { data: options, isLoading: isLoadingOptions } = useQuery(
    waitlistOptionsQueryKey,
    httpWaitlistOptions,
    { staleTime: Infinity },
  );

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<WaitlistFormData>({
    resolver: zodResolver(waitlistSchema),
    defaultValues: {
      whatsappNumber: "",
      fullName: "",
      email: "",
      referralTag: "",
      tradeFrequency: "",
      primaryCryptoActivity: "",
      monthlyVolumeRange: "",
      frustrations: [],
      removeForever: "",
      agreeToUpdates: false,
    },
  });

  const selectedFrustrations = watch("frustrations") || [];

  // Drives the submit button's active styling. Deliberately a "has every
  // required field been answered" check rather than formState.isValid, which
  // would need mode: "onChange" and start flagging errors while typing.
  const formValues = watch();
  const isFormFilled = Boolean(
    formValues.whatsappNumber?.trim() &&
    formValues.fullName?.trim() &&
    formValues.email?.trim() &&
    formValues.tradeFrequency &&
    formValues.primaryCryptoActivity &&
    formValues.monthlyVolumeRange &&
    formValues.frustrations?.length &&
    formValues.removeForever?.trim() &&
    formValues.agreeToUpdates,
  );

  const handleFrustrationToggle = (value: string) => {
    const current = [...selectedFrustrations];
    const index = current.indexOf(value);
    if (index > -1) {
      current.splice(index, 1);
    } else {
      current.push(value);
    }
    setValue("frustrations", current, { shouldValidate: true });
  };

  const onSubmit = async (data: WaitlistFormData) => {
    setIsPending(true);
    try {
      const response = await httpJoinWaitlist({
        fullName: data.fullName,
        email: data.email,
        whatsappNumber: data.whatsappNumber,
        referralTag: data.referralTag,
        tradeFrequency: data.tradeFrequency,
        primaryCryptoActivity: data.primaryCryptoActivity,
        monthlyVolumeRange: data.monthlyVolumeRange,
        frustrations: data.frustrations,
        removeForever: data.removeForever,
      });

      // A duplicate email or WhatsApp number is a success, not an error. The
      // server returns the existing waitlistNumber in that case, so the modal
      // can still show them their position.
      setAlreadyOnWaitlist(Boolean(response?.data?.alreadyOnWaitlist));
      setWaitlistPosition(response?.data?.waitlistNumber ?? null);
      setIsConfirmationModalOpen(true);
      reset();
    } catch (error) {
      // The network layer already surfaces the server's field errors and
      // messages as toasts; leave the form filled so it can be corrected.
    } finally {
      setIsPending(false);
    }
  };

  const scrollToForm = (e: React.MouseEvent) => {
    e.preventDefault();
    const formElement = document.getElementById("waitlist-form-section");
    if (formElement) {
      formElement.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div
      className="min-h-screen overflow-x-hidden text-white bg-black select-none"
      style={{ fontFamily: "'Bricolage Grotesque', system-ui, sans-serif" }}
    >
      <Helmet>
        <title>Bitoshi Africa | Join our V2 Waitlist</title>
        <meta
          name="description"
          content="Get in before everyone else. Join our V2 waitlist to unlock early access, exclusive rewards, priority features, and launch-day bonuses reserved for you."
        />
        <meta
          name="keywords"
          content="Bitoshi V2, Bitoshi, Bitoshi Africa, Crypto Waitlist, Bitcoin Africa"
        />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link
          rel="preconnect"
          href="https://fonts.gstatic.com"
          crossOrigin="anonymous"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:wght@200..800&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      <Header />

      {/* HERO SECTION */}
      <section className="relative pt-32 pb-20 px-6 md:px-12 lg:px-20 min-h-[100vh] lg:min-h-[940px] flex flex-col justify-center items-center lg:items-start bg-[linear-gradient(180deg,_#000000_0%,_#1f1f1f_290%)] overflow-hidden">
        {/* Abstract background graphics */}
        <div className="absolute top-[20%] right-[-10%] w-[500px] h-[500px] bg-[#f7931a]/5 rounded-full blur-[120px] pointer-events-none" />

        <div className="grid items-center w-full grid-cols-1 gap-12 lg:grid-cols-12">
          <div className="flex flex-col items-center gap-8 text-center lg:col-span-6 lg:text-left lg:items-start">
            <div className="text-[#f7931a] text-xs md:text-sm font-semibold tracking-wider uppercase">
              Bitoshi V2 Waitlist
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.05] max-w-[600px]">
              The Future of Crypto in Africa Starts Here
            </h1>
            <p className="text-lg md:text-xl text-white/80 font-light max-w-[550px] leading-relaxed">
              Get in before everyone else. Join our V2 waitlist to unlock early
              access, exclusive rewards, priority features, and launch-day
              bonuses reserved for you.
            </p>
            <button
              onClick={scrollToForm}
              className="bg-white text-black font-semibold text-lg md:text-xl px-10 py-4 rounded-full shadow-lg hover:bg-opacity-90 transition-all transform hover:scale-[1.02] active:scale-[0.98] cursor-pointer"
            >
              Join the Waitlist
            </button>
          </div>

          {/* Hero image */}
          <div className="lg:col-span-6 flex justify-center lg:justify-end items-center relative h-[350px] md:h-[500px] lg:h-[750px] w-full lg:-mr-20 overflow-hidden">
            <img
              src="/images/v2-waitlist-hero.webp"
              alt="Bitoshi V2 Hero Illustration"
              className="object-contain max-h-full max-w-full drop-shadow-[0_20px_50px_rgba(247,147,26,0.15)] transform translate-y-10 lg:translate-y-24"
            />
          </div>
        </div>
      </section>

      {/* MOCKUP SHOWCASE & SOCIAL PROOF */}
      <section className="relative z-10 py-24 -mt-10 overflow-hidden bg-transparent lg:-mt-64">
        <div className="max-w-[1280px] w-full mx-auto px-6 md:px-12 lg:px-20 text-center flex flex-col">
          <div className="relative min-h-[500px] md:min-h-[700px] lg:min-h-[600px] flex flex-col lg:flex-row justify-center items-center gap-12">
            {/* Left Mockup & Badge */}
            <div className="relative flex flex-col items-center group lg:absolute lg:left-[5%] xl:left-[10%] lg:top-[50px]">
              {/* Floating Rotated Badge */}
              <div className="absolute top-[45%] lg:top-[35%] left-[-20px] lg:left-[-60px] w-[200px] md:w-[220px] bg-[#f7931b] border border-black/10 text-white font-medium text-sm md:text-base px-4 py-3 rounded-2xl shadow-xl transform -rotate-[18.367deg] z-10 select-none text-center">
                “2000+ Nigerians are already waiting”
              </div>
              <img
                src="/images/v2-iphone-left.webp"
                alt="Bitoshi V2 App Interface Left Mockup"
                className="w-[280px] md:w-[340px] lg:w-[410px] xl:w-[430px] object-contain drop-shadow-[0_25px_40px_rgba(0,0,0,0.6)] transform lg:-rotate-[10.808deg]"
              />
            </div>

            {/* Right Mockup & Badge */}
            <div className="relative flex flex-col items-center group lg:absolute lg:right-[5%] xl:right-[10%] lg:top-[150px]">
              {/* Floating Rotated Badge */}
              <div className="absolute top-[40%] lg:top-[40%] right-[-20px] lg:right-[-80px] w-[280px] md:w-[335px] bg-[#f7591a] border border-black/10 text-white font-medium text-sm md:text-base px-4 py-3 rounded-2xl shadow-xl transform rotate-[14.49deg] z-10 select-none text-center">
                “Join the traders who have done ₦500M in volume on V1”
              </div>
              <img
                src="/images/v2-iphone-right.webp"
                alt="Bitoshi V2 App Interface Right Mockup"
                className="w-[290px] md:w-[350px] lg:w-[440px] xl:w-[460px] object-contain drop-shadow-[0_25px_40px_rgba(0,0,0,0.6)] transform lg:rotate-[14.732deg]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FORM SECTION */}
      <section
        id="waitlist-form-section"
        className="relative py-24 px-6 md:px-12 lg:px-20 bg-[linear-gradient(180deg,_#000000_60%,_#361e00_153%)] flex flex-col items-center"
      >
        <div className="max-w-[860px] w-full flex flex-col gap-12">
          {/* Form Card Container */}
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex flex-col gap-8"
          >
            {/* WhatsApp Number */}
            <div className="flex flex-col gap-2">
              <label
                htmlFor="whatsappNumber"
                className="flex items-center gap-1 text-lg font-medium"
              >
                WhatsApp number{" "}
                <span className="font-bold text-red-500">*</span>
              </label>
              <input
                id="whatsappNumber"
                type="text"
                placeholder="080xxxxxxxx"
                {...register("whatsappNumber")}
                className={`bg-[#222222] border ${errors.whatsappNumber ? "border-red-500/80" : "border-[#3a3a3a]"} focus:border-[#f7931a] focus:ring-0 outline-none text-white text-base md:text-lg rounded-lg px-4 py-3.5 transition-colors placeholder:text-white/30 w-full`}
              />
              {errors.whatsappNumber && (
                <p className="mt-1 text-sm text-red-500/90">
                  {errors.whatsappNumber.message}
                </p>
              )}
            </div>

            {/* Full Name */}
            <div className="flex flex-col gap-2">
              <label
                htmlFor="fullName"
                className="flex items-center gap-1 text-lg font-medium"
              >
                Full name <span className="font-bold text-red-500">*</span>
              </label>
              <input
                id="fullName"
                type="text"
                placeholder="e.g Taiwo Ndubisi"
                {...register("fullName")}
                className={`bg-[#222222] border ${errors.fullName ? "border-red-500/80" : "border-[#3a3a3a]"} focus:border-[#f7931a] focus:ring-0 outline-none text-white text-base md:text-lg rounded-lg px-4 py-3.5 transition-colors placeholder:text-white/30 w-full`}
              />
              {errors.fullName && (
                <p className="mt-1 text-sm text-red-500/90">
                  {errors.fullName.message}
                </p>
              )}
            </div>

            {/* Email Address */}
            <div className="flex flex-col gap-2">
              <label
                htmlFor="email"
                className="flex items-center gap-1 text-lg font-medium"
              >
                Email address <span className="font-bold text-red-500">*</span>
              </label>
              <input
                id="email"
                type="email"
                placeholder="xyz@gmail.com"
                {...register("email")}
                className={`bg-[#222222] border ${errors.email ? "border-red-500/80" : "border-[#3a3a3a]"} focus:border-[#f7931a] focus:ring-0 outline-none text-white text-base md:text-lg rounded-lg px-4 py-3.5 transition-colors placeholder:text-white/30 w-full`}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-500/90">
                  {errors.email.message}
                </p>
              )}
            </div>

            {/* Referral Source */}
            <div className="flex flex-col gap-2">
              <label
                htmlFor="referralTag"
                className="flex items-center gap-1 text-lg font-medium"
              >
                Source of referral: (e.g. X, Instagram, Friend, WhatsApp,
                Bitoshi Username)
              </label>
              <input
                id="referralTag"
                type="text"
                placeholder="How did you hear about Bitoshi?"
                {...register("referralTag")}
                className={`bg-[#222222] border ${errors.referralTag ? "border-red-500/80" : "border-[#3a3a3a]"} focus:border-[#f7931a] focus:ring-0 outline-none text-white text-base md:text-lg rounded-lg px-4 py-3.5 transition-colors placeholder:text-white/30 w-full`}
              />
              {errors.referralTag && (
                <p className="mt-1 text-sm text-red-500/90">
                  {errors.referralTag.message}
                </p>
              )}
            </div>

            {/* Trade Frequency Dropdown */}
            <div className="flex flex-col gap-2">
              <label
                htmlFor="tradeFrequency"
                className="flex items-center gap-1 text-lg font-medium"
              >
                How often do you trade?{" "}
                <span className="font-bold text-red-500">*</span>
              </label>
              <select
                id="tradeFrequency"
                disabled={isLoadingOptions}
                {...register("tradeFrequency")}
                className={`bg-[#222222] border ${errors.tradeFrequency ? "border-red-500/80" : "border-[#3a3a3a]"} focus:border-[#f7931a] focus:ring-0 outline-none text-white text-base md:text-lg rounded-lg px-4 py-3.5 transition-colors w-full cursor-pointer appearance-none disabled:opacity-60 disabled:cursor-wait`}
                style={{
                  backgroundImage: `url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "right 1rem center",
                  backgroundSize: "1.2em",
                }}
              >
                <option value="" disabled className="text-white/50">
                  {isLoadingOptions ? "Loading options..." : "Select frequency"}
                </option>
                {options?.tradeFrequency?.map((option) => (
                  <option
                    key={option.value}
                    value={option.value}
                    className="bg-[#151515]"
                  >
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.tradeFrequency && (
                <p className="mt-1 text-sm text-red-500/90">
                  {errors.tradeFrequency.message}
                </p>
              )}
            </div>

            {/* Primary Crypto Activity Dropdown */}
            <div className="flex flex-col gap-2">
              <label
                htmlFor="primaryCryptoActivity"
                className="flex items-center gap-1 text-lg font-medium"
              >
                What is your primary crypto activity?{" "}
                <span className="font-bold text-red-500">*</span>
              </label>
              <select
                id="primaryCryptoActivity"
                disabled={isLoadingOptions}
                {...register("primaryCryptoActivity")}
                className={`bg-[#222222] border ${errors.primaryCryptoActivity ? "border-red-500/80" : "border-[#3a3a3a]"} focus:border-[#f7931a] focus:ring-0 outline-none text-white text-base md:text-lg rounded-lg px-4 py-3.5 transition-colors w-full cursor-pointer appearance-none disabled:opacity-60 disabled:cursor-wait`}
                style={{
                  backgroundImage: `url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "right 1rem center",
                  backgroundSize: "1.2em",
                }}
              >
                <option value="" disabled className="text-white/50">
                  {isLoadingOptions
                    ? "Loading options..."
                    : "Select primary activity"}
                </option>
                {options?.primaryCryptoActivity?.map((option) => (
                  <option
                    key={option.value}
                    value={option.value}
                    className="bg-[#151515]"
                  >
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.primaryCryptoActivity && (
                <p className="mt-1 text-sm text-red-500/90">
                  {errors.primaryCryptoActivity.message}
                </p>
              )}
            </div>

            {/* Monthly Volume Range Dropdown */}
            <div className="flex flex-col gap-2">
              <label
                htmlFor="monthlyVolumeRange"
                className="flex items-center gap-1 text-lg font-medium"
              >
                How much do you trade in a typical month?{" "}
                <span className="font-bold text-red-500">*</span>
              </label>
              <select
                id="monthlyVolumeRange"
                disabled={isLoadingOptions}
                {...register("monthlyVolumeRange")}
                className={`bg-[#222222] border ${errors.monthlyVolumeRange ? "border-red-500/80" : "border-[#3a3a3a]"} focus:border-[#f7931a] focus:ring-0 outline-none text-white text-base md:text-lg rounded-lg px-4 py-3.5 transition-colors w-full cursor-pointer appearance-none disabled:opacity-60 disabled:cursor-wait`}
                style={{
                  backgroundImage: `url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "right 1rem center",
                  backgroundSize: "1.2em",
                }}
              >
                <option value="" disabled className="text-white/50">
                  {isLoadingOptions
                    ? "Loading options..."
                    : "Select monthly volume"}
                </option>
                {options?.monthlyVolumeRange?.map((option) => (
                  <option
                    key={option.value}
                    value={option.value}
                    className="bg-[#151515]"
                  >
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.monthlyVolumeRange && (
                <p className="mt-1 text-sm text-red-500/90">
                  {errors.monthlyVolumeRange.message}
                </p>
              )}
            </div>

            {/* Pain Points Checkboxes */}
            <div className="flex flex-col gap-4">
              <label className="flex items-center gap-1 text-lg font-medium">
                What are your biggest pain points with crypto platforms? (Select
                all that apply){" "}
                <span className="font-bold text-red-500">*</span>
              </label>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {isLoadingOptions && (
                  <p className="text-base text-white/50">Loading options...</p>
                )}
                {options?.frustrations?.map((option) => {
                  const isChecked = selectedFrustrations.includes(option.value);
                  return (
                    <div
                      key={option.value}
                      onClick={() => handleFrustrationToggle(option.value)}
                      className={`flex items-center gap-3 bg-[#222222] border ${isChecked ? "border-[#f7931a] bg-[#f7931a]/5" : "border-[#3a3a3a]"} hover:border-[#f7931a]/60 rounded-xl px-4 py-4 transition-all cursor-pointer`}
                    >
                      <div
                        className={`w-6 h-6 rounded-md border flex justify-center items-center transition-all ${isChecked ? "bg-[#f7931a] border-[#f7931a]" : "border-[#555555] bg-transparent"}`}
                      >
                        {isChecked && (
                          <svg
                            className="w-4 h-4 text-black fill-current"
                            viewBox="0 0 20 20"
                          >
                            <path d="M0 11l2-2 5 5L18 3l2 2L7 18z" />
                          </svg>
                        )}
                      </div>
                      <span className="text-base text-white/95 md:text-lg">
                        {option.label}
                      </span>
                    </div>
                  );
                })}
              </div>
              {errors.frustrations && (
                <p className="mt-1 text-sm text-red-500/90">
                  {errors.frustrations.message}
                </p>
              )}
            </div>

            {/* Remove One Thing Textarea */}
            <div className="flex flex-col gap-2">
              <label
                htmlFor="removeForever"
                className="flex items-center gap-1 text-lg font-medium"
              >
                If you could remove one thing from crypto trading forever, what
                would it be? <span className="font-bold text-red-500">*</span>
              </label>
              <textarea
                id="removeForever"
                placeholder="Name one thing you could remove from crypto trading forever..."
                rows={4}
                maxLength={500}
                {...register("removeForever")}
                className={`bg-[#222222] border ${errors.removeForever ? "border-red-500/80" : "border-[#3a3a3a]"} focus:border-[#f7931a] focus:ring-0 outline-none text-white text-base md:text-lg rounded-lg px-4 py-3.5 transition-colors placeholder:text-white/30 w-full resize-none`}
              />
              {errors.removeForever && (
                <p className="mt-1 text-sm text-red-500/90">
                  {errors.removeForever.message}
                </p>
              )}
            </div>

            {/* Terms and updates consent */}
            <div className="flex flex-col gap-2">
              <div
                onClick={() =>
                  setValue("agreeToUpdates", !watch("agreeToUpdates"), {
                    shouldValidate: true,
                  })
                }
                className="flex items-start gap-3 cursor-pointer"
              >
                <div
                  className={`w-5 h-5 mt-1 shrink-0 rounded-md border flex justify-center items-center transition-all ${watch("agreeToUpdates") ? "bg-[#f7931a] border-[#f7931a]" : "border-[#555555] bg-transparent"}`}
                >
                  {watch("agreeToUpdates") && (
                    <svg
                      className="w-3.5 h-3.5 text-black fill-current"
                      viewBox="0 0 20 20"
                    >
                      <path d="M0 11l2-2 5 5L18 3l2 2L7 18z" />
                    </svg>
                  )}
                </div>
                <input
                  type="checkbox"
                  className="hidden"
                  {...register("agreeToUpdates")}
                />
                <span className="text-sm leading-snug text-white/80 md:text-base">
                  I agree to receive updates about Bitoshi V2 via Email and
                  WhatsApp.
                </span>
              </div>
              {errors.agreeToUpdates && (
                <p className="mt-1 text-sm text-red-500/90">
                  {errors.agreeToUpdates.message}
                </p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isPending || isLoadingOptions}
              className={`${isFormFilled ? "bg-white" : "bg-[#a1a1a1]"} hover:bg-white text-black font-semibold text-lg md:text-xl py-4 rounded-full transition-colors flex justify-center items-center gap-2 cursor-pointer shadow-lg mt-4 disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {isPending ? (
                <>
                  <Spinner />
                  <span>Submitting...</span>
                </>
              ) : (
                "Submit"
              )}
            </button>
          </form>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-black border-t border-[#111111] px-6 py-16 md:px-12 lg:px-20 text-white">
        <div className="max-w-[1280px] w-full mx-auto grid grid-cols-2 md:grid-cols-5 gap-8 md:gap-12">
          {/* Products Column */}
          <div className="flex flex-col gap-4">
            <h4 className="text-base font-bold tracking-wider text-white uppercase">
              Products
            </h4>
            <ul className="flex flex-col gap-2.5">
              <li>
                <Link
                  to={publicRoutes.home}
                  className="text-sm transition-colors text-white/60 hover:text-white"
                >
                  Trade
                </Link>
              </li>
              <li>
                <span className="text-sm opacity-50 cursor-not-allowed text-white/60">
                  Virtual Card
                </span>
              </li>
              <li>
                <span className="text-sm opacity-50 cursor-not-allowed text-white/60">
                  OTC Trading
                </span>
              </li>
              <li>
                <span className="text-sm opacity-50 cursor-not-allowed text-white/60">
                  Bitoshi Pay
                </span>
              </li>
              <li>
                <span className="text-sm opacity-50 cursor-not-allowed text-white/60">
                  Swap
                </span>
              </li>
              <li>
                <Link
                  to={publicRoutes.externalTrade}
                  className="text-sm transition-colors text-white/60 hover:text-white"
                >
                  External buy/sell
                </Link>
              </li>
              <li>
                <span className="text-sm opacity-50 cursor-not-allowed text-white/60">
                  Vendors
                </span>
              </li>
            </ul>
          </div>

          {/* Company Column */}
          <div className="flex flex-col gap-4">
            <h4 className="text-base font-bold tracking-wider text-white uppercase">
              Company
            </h4>
            <ul className="flex flex-col gap-2.5">
              <li>
                <Link
                  to={publicRoutes.about}
                  className="text-sm transition-colors text-white/60 hover:text-white"
                >
                  About us
                </Link>
              </li>
              <li>
                <span className="text-sm opacity-50 cursor-not-allowed text-white/60">
                  Careers
                </span>
              </li>
              <li>
                <a
                  href="/blog"
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm transition-colors text-white/60 hover:text-white"
                >
                  Blog
                </a>
              </li>
              <li>
                <a
                  href="#newsletter"
                  className="text-sm transition-colors text-white/60 hover:text-white"
                >
                  Newsletter
                </a>
              </li>
            </ul>
          </div>

          {/* Support Column */}
          <div className="flex flex-col gap-4">
            <h4 className="text-base font-bold tracking-wider text-white uppercase">
              Support
            </h4>
            <ul className="flex flex-col gap-2.5">
              <li>
                <Link
                  to={publicRoutes.helpcenter}
                  className="text-sm transition-colors text-white/60 hover:text-white"
                >
                  Help center
                </Link>
              </li>
              <li>
                <span className="text-sm opacity-50 cursor-not-allowed text-white/60">
                  Video tutorials
                </span>
              </li>
              <li>
                <span className="text-sm opacity-50 cursor-not-allowed text-white/60">
                  FAQ
                </span>
              </li>
            </ul>
          </div>

          {/* Legal Column */}
          <div className="flex flex-col gap-4">
            <h4 className="text-base font-bold tracking-wider text-white uppercase">
              Legal
            </h4>
            <ul className="flex flex-col gap-2.5">
              <li>
                <Link
                  to={publicRoutes.terms}
                  className="text-sm transition-colors text-white/60 hover:text-white"
                >
                  Terms of Use
                </Link>
              </li>
              <li>
                <span className="text-sm opacity-50 cursor-not-allowed text-white/60">
                  User Risk Disclosure
                </span>
              </li>
              <li>
                <Link
                  to={publicRoutes.privacyPolicy}
                  className="text-sm transition-colors text-white/60 hover:text-white"
                >
                  Privacy Policy
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Column */}
          <div className="flex flex-col col-span-2 gap-4 md:col-span-1">
            <h4 className="text-base font-bold tracking-wider text-white uppercase">
              Contact
            </h4>
            <ul className="flex flex-col gap-2.5">
              <li>
                <a
                  href="mailto:hello@bitoshi.africa"
                  className="text-sm break-words transition-colors text-white/60 hover:text-white"
                >
                  hello@bitoshi.africa
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="max-w-[1280px] w-full mx-auto mt-16 pt-8 border-t border-[#111111] flex flex-col md:flex-row justify-between items-center gap-4 text-white/40 text-xs">
          <p>
            © {new Date().getFullYear()} Bitoshi Africa. All rights reserved.
          </p>
          <div className="flex gap-6">
            <a
              href="https://twitter.com/Bitoshiafrica"
              target="_blank"
              rel="noreferrer"
              className="transition-colors hover:text-white"
            >
              Twitter
            </a>
            <a
              href="https://instagram.com/bitoshi.africa"
              target="_blank"
              rel="noreferrer"
              className="transition-colors hover:text-white"
            >
              Instagram
            </a>
            <a
              href="https://www.linkedin.com/company/bitoshiafrica"
              target="_blank"
              rel="noreferrer"
              className="transition-colors hover:text-white"
            >
              LinkedIn
            </a>
          </div>
        </div>
      </footer>

      {/* Waitlist Confirmation Modal */}
      <WaitlistConfirmationModal
        isOpen={isConfirmationModalOpen}
        position={waitlistPosition}
        alreadyOnWaitlist={alreadyOnWaitlist}
        onClose={() => setIsConfirmationModalOpen(false)}
        getAppUrl={BITOSHI_DOWNLOAD_URL}
        whatsappUrl={WHATSAPP_COMMUNITY_URL}
        telegramUrl={TELEGRAM_COMMUNITY_URL}
        twitterUrl={TWITTER_URL}
      />
    </div>
  );
};

export default V2Waitlist;
